import jwt, { Jwt, JwtPayload } from 'jsonwebtoken';

export interface BaseJwtPayload extends JwtPayload {}

/**
 * A helper class for JWTs.
 */
export class JwtHelper {
  /**
   * Decodes a signed payload.
   * @param signedPayload The signed payload to decode.
   * @param secret The secret to decode the payload with.
   */
  public static decodePayload<T extends BaseJwtPayload>(
    signedPayload: string,
    secret: string,
  ): T {
    const expirationEpochSeconds = (jwt.decode(signedPayload) as BaseJwtPayload)
      ?.exp;
    if (!expirationEpochSeconds) {
      throw new Error('JWT has no expiration');
    }
    return jwt.verify(signedPayload, secret) as T;
  }

  /**
   * Gets the issuer from the payload. This is "unsafe" because it does not <strong>verify the signature</strong>.
   * @param payload The payload to decode.
   */
  public static unsafeGetIssuer(payload: string): string | undefined {
    return (jwt.decode(payload) as BaseJwtPayload)?.iss;
  }

  /**
   * Decodes the token but does not verify the signature. This is "unsafe" because it does not <strong>verify the signature</strong>.
   * @param payload The payload to decode.
   */
  public static unsafeDecodePayload<T extends BaseJwtPayload>(
    payload: string,
  ): T | undefined {
    return jwt.decode(payload) as T | undefined;
  }

  /**
   * Decodes the token but does not verify the signature. This is "unsafe" because it does not <strong>verify the signature</strong>.
   * @param token The token to decode.
   */
  public static unsafeDecodeToken(token: string): Jwt | undefined {
    return jwt.decode(token, { complete: true }) as Jwt | undefined;
  }
}
